import { Box, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { addDays, format } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

interface PlacementAvailabilitySelectorProps {
  selectableNumberOfDays: number;
  maxVisibleDates?: number;
}

export function PlacementAvailabilitySelector(props: PlacementAvailabilitySelectorProps) {
  const { selectableNumberOfDays, maxVisibleDates = 4 } = props;

  const { tmz: workerTimezone } = useDefinedWorker();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const today = utcToZonedTime(new Date(), workerTimezone);

  const allSelectableDates: Date[] = useMemo(() => {
    return Array.from({ length: maxVisibleDates }, (_, index) => addDays(today, index + 1));
  }, [today, maxVisibleDates]);

  // --- Horizontal scrolling helpers (similar to ShiftCalendarSmall) ---
  const datesContainerRef = useRef<HTMLDivElement>(null);
  const selectedDateRef = useRef<HTMLDivElement>(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const handleScrollPositionCheck = () => {
    const container = datesContainerRef.current;
    if (!container) {
      return;
    }

    setIsAtStart(container.scrollLeft === 0);
    setIsAtEnd(container.scrollLeft + container.clientWidth >= container.scrollWidth - 1);
  };

  useEffect(() => {
    const container = datesContainerRef.current;
    if (!container) {
      return undefined;
    }

    handleScrollPositionCheck();
    container.addEventListener("scroll", handleScrollPositionCheck);

    return () => {
      container.removeEventListener("scroll", handleScrollPositionCheck);
    };
  }, []);

  const scrollByAmount = (amount: number) => {
    const container = datesContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollBy({ left: amount, behavior: "smooth" });
  };

  const scrollLeft = () => {
    scrollByAmount(-300);
  };

  const scrollRight = () => {
    scrollByAmount(300);
  };

  return (
    <Controller
      name="availability"
      control={control}
      defaultValue={[]}
      render={({ field }) => {
        const selectedDates: Date[] = (field.value ?? []) as Date[];

        const isDateSelected = (date: Date) => {
          const key = format(date, "yyyy-MM-dd");
          return selectedDates.some((d) => format(d, "yyyy-MM-dd") === key);
        };

        const toggleDateSelection = (date: Date) => {
          const key = format(date, "yyyy-MM-dd");
          const currentlySelected = selectedDates.some((d) => format(d, "yyyy-MM-dd") === key);

          if (currentlySelected) {
            field.onChange(selectedDates.filter((d) => format(d, "yyyy-MM-dd") !== key));
            return;
          }

          // If max reached, drop the earliest selected date to accommodate the new one
          if (selectedDates.length >= selectableNumberOfDays) {
            // Sort a copy chronologically and remove the earliest
            const remainingSelections = [...selectedDates]
              .sort((a, b) => a.getTime() - b.getTime())
              .slice(1);

            field.onChange([...remainingSelections, date]);
            return;
          }

          field.onChange([...selectedDates, date]);
        };

        return (
          <Stack spacing={4} alignItems="flex-start">
            <Text variant="body1">Available dates</Text>

            {selectedDates.length > 0 && (
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                Select up to {selectableNumberOfDays} {pluralize("date", selectableNumberOfDays)}
              </Text>
            )}
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              {!isAtStart && (
                <IconButton
                  size="xSmall"
                  variant="outlined"
                  iconType="chevron-left"
                  disabled={isAtStart}
                  sx={{ mr: 1, "&:disabled": { opacity: 0 } }}
                  onClick={scrollLeft}
                />
              )}

              <Box
                ref={datesContainerRef}
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  scrollbarWidth: "none",
                  flexGrow: 1,
                  scrollBehavior: "smooth",
                }}
              >
                {allSelectableDates.map((date) => {
                  const selected = isDateSelected(date);
                  return (
                    <Stack
                      key={format(date, "yyyy-MM-dd")}
                      ref={selected ? selectedDateRef : undefined}
                      spacing={1}
                      sx={{
                        flex: "0 0 auto",
                        minWidth: 48,
                        mr: 2,
                        py: 2,
                        borderRadius: 2,
                        textAlign: "center",
                        border: (theme) =>
                          selected
                            ? `1px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.secondary.main}`,
                      }}
                      onClick={() => {
                        toggleDateSelection(date);
                      }}
                    >
                      <Text variant="caption">{format(date, "MMM")}</Text>
                      <Text semibold variant="h6">
                        {format(date, "d")}
                      </Text>
                      <Text variant="caption">{format(date, "EEE")}</Text>
                    </Stack>
                  );
                })}
              </Box>

              {!isAtEnd && (
                <IconButton
                  size="xSmall"
                  variant="outlined"
                  iconType="chevron-right"
                  disabled={isAtEnd}
                  sx={{ ml: 1 }}
                  onClick={scrollRight}
                />
              )}
            </Box>

            {errors?.availability && (
              <Text variant="body2" color={(theme) => theme.palette.error.main}>
                {errors.availability?.message as string}
              </Text>
            )}
          </Stack>
        );
      }}
    />
  );
}
